import { v4 as uuidv4 } from 'uuid';
import { IClient } from './client-interface';
import { ClientOptions } from './client-options';

import { LoggerInterface,  } from '@aws-lambda-powertools/logger/types';
import { HttpClient } from '../http-client/http-client';
import { IHttpClient } from '../http-client/http-client-interface';
import { ClientError } from './client-error';
import { WflObject } from '../model/wfl/wflobject';
import { Relation, RelationType } from '../model/wfl/relation';
import { MetaDataValue } from '../model/wfl/metadata-value';
import { PropertyValue } from '../model/wfl/property-value';
import { Target } from '../model/wfl/target';

import { BodyInit } from "undici-types/fetch";

interface RegisterRequest {
    Appname: string,
    Url: string,
    Username: string,
    Password: string,
    RequestId: string,
    /**
     * Optional
     */
    ConfigurationId?: string
}

interface Responsebase {
    ConfigurationId: string,
    RequestId: string,
    EventSource: string,
    EventDetail: string,
    Status: string,
    StatusMessage?: string,
    Result: any
}



export class Client implements IClient{

    private _options: ClientOptions;
    private _logger : LoggerInterface;
    private _httpClient : IHttpClient;

    private _studioBaseUrl : string = "";

    private _publicationsCache : Record<string,any> = {};

    get options() {
        return this._options;
    }

    /**
     * The configurationId
     */
    private _configurationId: string | null = null;

    /**
     * @returns The configurationid
     * @throws QqAwsServiceStudioClientClientError when not logged on
     */
    get configurationId(): string {
        if (this._configurationId === null) {
             throw new ClientError("Not logged on")
        };

        return this._configurationId;
    }

    /**
     * 
     * @param logger The logger instance
     * @param options The Client options
     * @param httpClient The Http client. When not defined an instance will be created automatically.
     */
    constructor(logger : LoggerInterface, options: ClientOptions, httpClient? : IHttpClient) {

        console.log("Client::constructor", options);
        
        if (httpClient === undefined) {
            httpClient = new HttpClient(logger);
        }

        this._logger = logger;
        this._options = options;
        this._httpClient = httpClient;

        this._httpClient.setCommonHeaders( {
            'User-Agent': 'StudioClient Client',
            'x-api-key' : this.options.apiKey
        });

        this._httpClient.setBaseUrl(options.baseUrl);
    }

    private validateOptions(options: ClientOptions) {
        if (options.baseUrl == "") {
            throw new ClientError("baseUrl is required");
        }
    }

    /**
     * 
     * @returns true when logged in
     */
    isLoggedOn(): boolean {
        return this._configurationId !== null;
    }

    setStudioBaseUrl(url : string) {
        this._studioBaseUrl = this._sanitizeUrl(url);
    }

    /**
     * 
     * @param configurationId 
     * @returns true when logged in
     */
    async logon(configurationId: string) : Promise<boolean> {
        console.log(`QqAwsServiceStudioClientClient::logon: configurationId=[${configurationId}]`);
        //TODO: Maybe some additional validation here 

        this._configurationId = configurationId;

        return this.isLoggedOn();
    }


    /**
     * 
     * @returns The Woodwing Studio base URL
     */
    getBaseUrl(): string {
        return <string>this._options.baseUrl;
    }

    /**
     * @param studioUrl The 
     * @param appname The application name
     * @param username The WoodWing Studio username
     * @param password The WoodWing Studio password
     * @param requestId Optional: The requestID 
     * @param configurationId Optional: The configurationID
     * @returns The new or given ConfigurationId
     * @throws QqAwsServiceStudioClientClientError when the registration fails.
     */
    async register(studioUrl: string, appname: string, username: string, password: string, requestId: string|undefined, configurationId: string|undefined): Promise<string> {
        if (requestId === undefined) {
            requestId = this._generateRequestId();
        }
        console.log(`QqAwsServiceStudioClientClient::register: studioUrl=[${studioUrl}], appname=[${appname}], username=[${username}], requestId=[${requestId}]`);

        const request: RegisterRequest = {
            "Appname": appname,
            "Url": this._sanitizeUrl(studioUrl),
            "Username": username,
            "Password": password,
            "RequestId": requestId,
        };

        if (configurationId !== undefined) {
            if (this._configurationId !== null) {
                request.ConfigurationId = this._configurationId
            }
            request.ConfigurationId = configurationId
        }

        const result = await this._httpClient.request('POST', 'studioapplication', undefined, request);
        if ('ConfigurationId' in result) {
            this._configurationId = result.ConfigurationId;
            return result.ConfigurationId;
        }

        throw new ClientError("No 'ConfigurationId' in response");
    }

    async unRegister(configurationId?: string) : Promise<void> {
        if (configurationId === undefined) {
            configurationId = this.configurationId;
        }
    }

    /**
     * 
     * @param ids The publication ids to resolve
     * @param requestInfo What elements to retrieve. "PubChannels","States", "Categories"
     * @param forceNew When true, ignore local cache.
     * @returns All resolved publications 
     */
    public async getPublications(ids : string[], requestInfo? : string[], forceNew? : boolean, requestId?: string, configurationId?: string) : Promise<Record<string, any>[]> {
        if(forceNew === undefined) {
            forceNew = false;
        }
        
        console.log(`SqCreatePdf2Client::getPublications: ids=[${ids.join(', ')}], forceNew=[${forceNew}]`);
        let idsToRequest : string[] = [];
        for (const id of ids) {
            if (forceNew === true || !(id in this._publicationsCache)) {
                idsToRequest.push(id);
            } else {
                // Brand already cached
                this._logger.debug(`Brand ${id} already loaded. Check the request info`);
                if (requestInfo !== undefined) {
                    for (const requestInfoElement of requestInfo) {
                        if (!(requestInfoElement in this._publicationsCache[id])) {
                            // The request info element is not cached for this brand
                            this._logger.debug(`Brand ${id} already loaded. But ${requestInfoElement} was not requested before.`);
                            idsToRequest.push(id);
                            break;
                        }
                    }
                }

            }
        }

        if(idsToRequest.length > 0) {
            const publications = await this.getPublicationsInt(
                idsToRequest, requestInfo);

            for (const publication of publications) {
                const brandId = <string> publication.Id;
                this._publicationsCache[brandId] = publication;
            }
        }

        let result : Record<string, any>[] = [];
        for (const id of ids) {
            if (id in this._publicationsCache) {
                result.push(this._publicationsCache[id]);
            }
        }

        return result;
    }

    /**
     *
     * @param {string} publicationId
     * @returns {Promise<null|object>}
     */
    async getPublication(publicationId : string, requestInfo? : string[] ) {

        const publications = await this.getPublications([publicationId], requestInfo);
        

        for (let pubIdx in publications) {

            const publication = publications[pubIdx];

            console.log("processing publication: ", publication);

            if (publication.Id === publicationId.toString()) {
                return publication;
            }
        }

        return null;
    }

    /**
     * 
     * @param forceNew When true create a new ticket instead of the cached ticket
     * @param requestId 
     * @param configurationId 
     * @returns 
     */
    async getTicket(forceNew? : boolean, requestId?: string, configurationId?: string): Promise<string> {
        if (forceNew === undefined) {
            forceNew = false;
        }
        
        if (requestId === undefined) {
            requestId = this._generateRequestId();
        }

        if (configurationId === undefined) {
            configurationId = this.configurationId;
        }
        console.log(`QqAwsServiceStudioClientClient::getTicket: forceNew=[${forceNew}], requestId=[${requestId}], configurationId=[${configurationId}]`);

        const path = 'ticket';

        const query = new URLSearchParams({
            ConfigurationId: configurationId,
            RequestId: requestId,
            Forcenew: forceNew ? 'true' : 'false'
        })

        const result = await this._httpClient.request('GET', path, query);

        return result;
    }

    /**
     * @inheritdoc
     */
    async getStateByName(publicationId : string, type : string, name : string ) : Promise<null|any> {

        const publication = await this.getPublication(publicationId, ['States']);

        if (publication === null) {
            return null;
        }

        for (const state of publication.States) {
            //const state = publication.States[stateIdx];

            if (state.Type.toLowerCase() === type.toLowerCase() && state.Name.toLowerCase() === name.toLowerCase()) {
                return state;
            }
        }
        
        return null;
    }




    /**
     *
     * @param wflObject
     * @param childId
     * @param type
     * @param parentType
     * @returns 
     */
    async getWflRelations(wflObject : any, childId : string, type : string = 'Contained', parentType : string = 'Dossier') : Promise<object[]> {

        let result = [];

        for (let relIdx in wflObject.Relations) {
            const relation = wflObject.Relations[relIdx];

            if (
                relation.Child === childId &&
                relation.Type.toLowerCase() === type.toLowerCase() &&
                relation.ParentInfo.Type.toLowerCase() === parentType.toLowerCase()
            ) {
                result.push(relation)
            }

        }

        return result;
    }

    // /**
    //  *
    //  * @param {string} bucket
    //  * @param {string} key
    //  *
    //  * @returns {Promise<object>}
    //  */
    // async getDigitalArticle = async(bucket : string, key : string) : Promise<object> => {
    //     const s3client = new S3Client({});
    //     const response = await s3client.send(new GetObjectCommand(
    //         {
    //             Bucket: bucket,
    //             Key: key
    //         }
    //     ));

    //     if (!response.ETag) {
    //         throw new Error('Error getting digital article from bucket.');
    //     }

    //     return response;
    // }
    
    /**
     *
     * @param url
     * @returns 
     */
    async downloadFile(url : string, query? : URLSearchParams ) : Promise<ArrayBuffer>{
        if (query === undefined) {
            query = new URLSearchParams();
        }

        query.append('ticket', await this.getTicket());

        const buffer = await this._httpClient.downloadFile(url, query);
 
        return buffer;
    }
    
    
    /**
     * @inheritdoc
     */
    async uploadFile(body : BodyInit, contentType : string, size : number){

        if(this._studioBaseUrl === '') {
            throw new ClientError("No studio base url set");
        }

        const url = await this._httpClient.uploadFile(body, size, contentType, await this.getTicket(), this._studioBaseUrl )

        return url;
    }

    /**
     *
     * @param objectIds
     * @param rendition
     * @param requestInfo
     * @param lock
     * @param areas
     * @returns 
     */
    async getObjects(
        objectIds : string[], 
        rendition : string = 'native', 
        requestInfo : string[] = ['MetaData'],
        lock : boolean = false, 
        areas : string[] = ['Workflow']
    ) : Promise<WflObject[]> {

        const query = this._getCommonQueryParameters();
        query.append('ObjectIds', objectIds.join(','));
        query.append('RequestInfo', requestInfo.join(','));
        query.append('Rendition', rendition);

        const response = await this._httpClient.request('get', 'objects', query);
        //TODO: How to convert te result in a WflObject instance.
        return response;

    }

    async createObjects(wflObjects : WflObject[], lock : boolean = false) : Promise<WflObject[]> {

        const body = {
            WflObjects: wflObjects,
            RequestId: this._generateRequestId(),
            ConfigurationId: this.configurationId,
            Lock: lock
        }

        const response = await this._httpClient.request('post', 'objects', undefined, body);

        return response;
    }

    /**
     *
     * @param parentIdOrInstance
     * @param childIdsOrInstances
     * @param type
     * @returns 
     */
    async createObjectRelations(
        parentIdOrInstance : string | WflObject, 
        childIdsOrInstances : string[]|WflObject[], 
        type : RelationType = RelationType.Contained 
    ) : Promise<Relation[]> {
        const parentId = this._getObjectId(parentIdOrInstance);
        const childIds : string[] = [];

        for(const childIdOrInstance of childIdsOrInstances) {
            const childId = this._getObjectId(childIdOrInstance);
            childIds.push(childId);
        }

        let relations = [];

        for (const childId of childIds) {
            relations.push(new Relation({
                Parent: parentId,
                Child: childId,
                Type: type
            }));
        }

        const body = {
            RequestId : this._generateRequestId(),
            Relations : relations,
            ConfigurationId: this.configurationId
        }

        const response = await this._httpClient.request('post','objectrelations', undefined, body);
        return response;
    }

    /**
     * 
     * @param childIdsOrInstances 
     * @param permanent 
     * @param areas 
     * @returns 
     */
    async deleteObjects(childIdsOrInstances : string[] | WflObject[], permanent : boolean = false, areas : string = 'Workflow') : Promise<object> {
        const ids : string[] = [];
        for(const childIdOrInstance of childIdsOrInstances) {
            const childId = this._getObjectId(childIdOrInstance);
            ids.push(childId);
        }

        const query = this._getCommonQueryParameters();
        query.append('ObjectIds', ids.join(','));
        query.append('Permanent', '');
        query.append('Areas', areas);
        query.append('RequestId', this._generateRequestId());
        query.append('ConfigurationId', this.configurationId);

        const response = await this._httpClient.request('delete', 'objects', query);

        return response;
    }

    /**
     * 
     * @param action Workflow Action type.
     * @param context v10.40 Can be used to provide relevant information based on the dialog circumstances.
     * @param metaData v8.0: MetaData value allows client app to round trip the data.
     * @param targets v8.0: Object's targets. Data contained in Targets allow client app to round trip the data.
     * @param defaultDossier v7.0: Dossier ID: Request to populate the Dossier property. 
     *                       The given Publication and Issue are used to get dossiers (to choose from). If no Issue specified, 
     *                       first one is taken. The dossier ID will be used to set the default value at Dossier property. 
     *                       If DefaultDossier is nil (or left out) no dossier property nor dossiers will be returned.
     * @param parent v7.0: Parent ID: When creating objects that are placed (such as creating articles form layout) the client 
     *               knows that the object will be placed, but the server does not know yet. For placed objects, 
     *               the dialog has some fields disabled (greyed) like Publication/Brand, Issue and Category. Nil (or left out) 
     *               means object is not placed.
     * @param template v7.0: Template ID : When creating objects, some properties should be taken from a template. 
     *                 Those should be pre-filled in for the new object at the Create workflow dialog. Provide the object ID of 
     *                 the template (that was picked by user) to let server pre-fill properties. Nil (or left out) means object 
     *                 is not created from template.
     * @param areas v8.0: Area to search for the object. Nil means 'workflow' area only (for backwards compatibility reasons).
     * @param multipleObjects v9.2: Indicate if the dialog is for multiple objects or single object. 
     *                        Nil means 'false' (for backwards compatibility reasons).
     * @returns 
     */
    async getDialog2(
        action : string, 
        context? : string, 
        metaData? : MetaDataValue[], 
        targets? : Target[], 
        defaultDossier? : string,
        parent? : string,
        template? : string,
        areas? : string[],
        multipleObjects? : boolean
    ) {
        const body : Record<string, any> = {
            RequestId : this._generateRequestId(),
            ConfigurationId: this.configurationId,
            Action : action
        }

        if(context) {
            body['Context'] = context;
        }

        if(metaData) {
            //TODO: Must be 'MetaData' (bug in Service)
            body['Metadata'] = metaData;
        }

        if(targets) {
            body['Targets'] = targets;
        }

        if(defaultDossier) {
            body['DefaultDossier'] = defaultDossier;
        }

        if(parent) {
            body['Parent'] = parent;
        }

        if(template) {
            body['Template'] = template;
        }

        if(areas) {
            body['Areas'] = areas;
        }

        if(multipleObjects) {
            body['MultipleObjects'] = multipleObjects;
        }

        const response = await this._httpClient.request('post', 'dialog', undefined, body);
        return response;
    }

    /**
     * 
     * @param ids The brand id(s)
     * @param requestInfo What elements to retrieve. "PubChannels","States", "Categories"
     * @param requestId 
     * @param configurationId 
     * @returns 
     */
    private async getPublicationsInt(ids: string[], requestInfo? : string[], requestId?: string, configurationId?: string): Promise<Record<string, any>[]> {
        if(requestInfo === undefined) {
            requestInfo = [];
        }
        
        if (requestId === undefined) {
            requestId = this._generateRequestId();
        }
        console.log(`QqAwsServiceStudioClientClient::getPublications: ids=[${ids.join(',')}], requestInfo=[${requestInfo.join(', ')}], requestId=[${requestId}]`);

        if (configurationId === undefined) {
            configurationId = this.configurationId;
        }
        
        const query =  this._getCommonQueryParameters(configurationId);
        query.append('Ids', ids.join(','));
        query.append('RequestInfo', requestInfo.join(', '));

        const result = await this._httpClient.request('GET', 'publications', query);
        return result;
    }

    /**
     * Add the ConfigurationId and RequestId to the query
     * 
     * 
     * @param configurationId Overrule the configurationId when required.
     * @returns 
     */
    private _getCommonQueryParameters(configurationId? : string) : URLSearchParams {
        if (configurationId === undefined) {
            configurationId = this.configurationId;
        }
        const query = new URLSearchParams({
            ConfigurationId: configurationId,
            RequestId: this._generateRequestId()
        });

        return query;
    }

    /**
     * Get the parentId from the iddOrInstance
     *
     * @param iddOrInstance
     * @returns The object id
     * @throws ClientError when the id cannot be determined
     */
    private _getObjectId(idOrInstance : string | WflObject, ) : string {
        if(idOrInstance instanceof WflObject || (
            typeof idOrInstance === 'object' &&
            '__classname__' in idOrInstance &&
             idOrInstance['__classname__'] === 'Object')) {
            const id = idOrInstance.MetaData?.BasicMetaData?.ID;
            if(id === undefined || id === null) {
                throw new ClientError(`childId is undefined`);
            }
            return id;
        } else {
            return idOrInstance;
        }
    }

    _sanitizeUrl(url: string): string {
        return url.replace(/\/$/, "");
    }

    _generateRequestId() {
        return uuidv4();
    }


}